import { type File, useStores } from '@littlebird/sdk';
import { useCallback } from 'react';

import { renderCanvasPreview } from '@/features/canvas/canvas-export';
import { readCanvasHostTheme } from '@/features/canvas/canvas-theme';
import { electronApis } from '@/features/electron-api';
import { logError } from '@/lib/errorLogging';
import { useTheme } from '@/providers/ThemeProvider';

import type { NotificationRoute } from '@electron/shared/lb-notification.types';

export const REPORT_CARD = {
  width: 340,
  padding: 10,
  imageHeight: 340,
} as const;

const PREVIEW_IMAGE_WIDTH = REPORT_CARD.width - REPORT_CARD.padding * 2;
const PREVIEW_LAYOUT_WIDTH = 800;

const PREVIEW_MAX_HEIGHT = Math.ceil(
  (REPORT_CARD.imageHeight * PREVIEW_LAYOUT_WIDTH) / PREVIEW_IMAGE_WIDTH
);

export const useNotifyRoutineReport = () => {
  const { files: filesStore } = useStores();
  const { theme } = useTheme();

  return useCallback(
    async ({
      artifact,
      title,
      subtitle,
      route,
      notificationId = crypto.randomUUID(),
      signal,
    }: {
      artifact: File;
      title: string;
      subtitle?: string;
      route: NotificationRoute;
      notificationId?: string;
      signal?: AbortSignal;
    }): Promise<boolean> => {
      try {
        if (signal?.aborted) return false;
        const fileUrl = await filesStore.getFileUrl(artifact);
        if (!fileUrl || signal?.aborted) return false;
        const response = await fetch(fileUrl, { signal });
        if (!response.ok) return false;

        const preview = await renderCanvasPreview({
          canvasText: await response.text(),
          hostTheme: readCanvasHostTheme(theme),
          maxHeight: PREVIEW_MAX_HEIGHT,
          layoutWidth: PREVIEW_LAYOUT_WIDTH,
        });
        if (!preview.ok) {
          logError(
            new Error(preview.error),
            'Failed to render report preview',
            {
              context: 'useNotifyRoutineReport',
              metadata: { fileId: artifact.id },
            }
          );
          return false;
        }
        if (signal?.aborted) return false;

        await electronApis.lbNotifications.sendNotification({
          id: notificationId,
          view: 'routine-report',
          sound: 'done',
          // Transient notifications are dropped while the main window is focused
          payload: {
            title,
            subtitle,
            previewImage: preview.image,
            route,
          },
        });
        return true;
      } catch (error) {
        if (signal?.aborted) return false;
        logError(error, 'Failed to notify a routine report', {
          context: 'useNotifyRoutineReport',
          metadata: { fileId: artifact.id },
        });
        return false;
      }
    },
    [filesStore, theme]
  );
};
